import userService from './userService';
import attendanceService from './attendanceService';
import assignmentService from './assignmentService';
import examService from './examService';
import classService from './classService';
import api from './api';

const reportService = {
  /**
   * Get the logged-in student's own academic report (attendance, assignments, exams).
   * Requires student role.
   */
  getStudentReport: (params = {}) =>
    api.get("/reports/student", { params }),

  downloadStudentReportPdf: (params = {}) =>
    api.get("/reports/student/download", { params, responseType: "blob", headers: { Accept: "application/pdf" } }),

  getClassReport: async ({ class: className, subject, period } = {}) => {
    const result = {};

    // Students
    try {
      // Resolve class name to classId using classService
      let classId = null;
      try {
        const classesResp = await classService.getAllClasses({ page: 1, limit: 500 });
        const classes = classesResp?.data?.classes || classesResp?.classes || classesResp?.data || classesResp || [];
        const found = Array.isArray(classes) ? classes.find((c) => String(c.name) === String(className)) : null;
        if (found) classId = found.id || found.classId || null;
      } catch (e) {
        classId = null;
      }

      // Prefer institute-level students endpoint which superadmin/institute roles can access
      let studentsRes = null;
      try {
        // Use institute service endpoint which returns institute-wide students
        const instituteService = (await import('./instituteService')).default;
        studentsRes = await instituteService.getAllInstituteStudents({ page: 1, limit: 2000 });
        // If server returns 304 Not Modified, axios returns response; handle below
      } catch (e) {
        // If access forbidden (403) or endpoint missing, fallback to student endpoints
        try {
          if (classId) {
            studentsRes = await api.post('/student/get-students-by-class', { classId });
          } else {
            studentsRes = await api.get('/student/get-all-students', { params: { page: 1, limit: 2000 } });
          }
        } catch (e2) {
          studentsRes = null;
        }
      }

      const allStudents = studentsRes?.data?.data || studentsRes?.data || studentsRes || [];
      const studentsArr = Array.isArray(allStudents) ? allStudents : allStudents.students || [];
      // Filter by class when className is provided (match class name or classId)
      result.students = className
        ? studentsArr.filter(
            (s) =>
              String(s.className || s.class?.name || s.Class?.name || "").toLowerCase() ===
                String(className).toLowerCase() ||
              s.classId === classId
          )
        : studentsArr;
      result.stats = result.stats || {};
      result.stats.totalStudents = result.students.length;
    } catch (e) {
      // ignore
    }

    // Attendance (class-level)
    try {
      const attRes = await attendanceService.getClassAttendance(className, { period });
      const attData = attRes?.data?.data || attRes?.data || attRes;
      // Expecting format: { avgRate: number, present: n, absent: n, leave: n }
      if (attData) {
        result.attendance = attData;
        result.stats = result.stats || {};
        if (typeof attData.avgRate === 'number') result.stats.avgAttendance = attData.avgRate;
      }
    } catch (e) {
      // ignore
    }

    // Assignments - students submitted percentage
    try {
      const assignRes = await assignmentService.getStudentsSubmittedAssignments({
        class: className,
        subject,
        period,
        limit: 2000,
      });
      const payload = assignRes?.data?.data || assignRes?.data || assignRes;
      const assignData = Array.isArray(payload) ? payload : payload?.submissions || [];
      if (assignData.length >= 0) {
        const submittedCount = assignData.length;
        const total = result.stats?.totalStudents || result.students?.length || 0;
        result.stats = result.stats || {};
        result.stats.assignmentsCompleted = total ? Math.round((submittedCount / total) * 100) : 0;
        result.assignments = assignData;
      }
    } catch (e) {
      // ignore - use defaults on error
    }

    // Exams / Results - compute avg marks, grade distribution, top students
    try {
      const examRes = await examService.getExamResults({ class: className, subject, period });
      const examData = examRes?.data?.data || examRes?.data || examRes || [];
      const rows = Array.isArray(examData) ? examData : examData.results || [];

      if (rows.length) {
        // rows expected: [{ studentId, studentName, marksObtained, maxMarks, examName }]
        const byStudent = {};
        rows.forEach((r) => {
          const sid = r.studentId || r.id || r.userId || r.student?.id;
          const name = r.studentName || r.student?.fullName || r.student?.name || r.fullName || r.name;
          const marks = Number(r.marksObtained ?? r.marks ?? r.score ?? 0);
          const max = Number(r.maxMarks ?? r.max ?? r.total ?? 100);
          if (!byStudent[sid]) byStudent[sid] = { id: sid, name, total: 0, max: 0, count: 0 };
          byStudent[sid].total += marks;
          byStudent[sid].max += max;
          byStudent[sid].count += 1;
        });
        // Resolve "Unknown" names from result.students
        result.students?.forEach((s) => {
          const sid = s.id || s.studentId;
          const lookup = byStudent[sid];
          if (lookup && (!lookup.name || lookup.name === "Unknown")) {
            lookup.name = s.fullName || s.name || s.user?.fullName || [s.firstName, s.lastName].filter(Boolean).join(" ").trim() || lookup.name;
          }
        });

        const computed = Object.values(byStudent).map((s) => ({
          id: s.id,
          name: s.name,
          avg: s.max ? Math.round((s.total / s.max) * 100) : 0,
        }));

        computed.sort((a, b) => b.avg - a.avg);
        result.topStudents = computed.slice(0, 10);

        // grade buckets
        const buckets = { '90-100': 0, '80-89': 0, '70-79': 0, '60-69': 0, 'Below 60': 0 };
        computed.forEach((s) => {
          if (s.avg >= 90) buckets['90-100']++;
          else if (s.avg >= 80) buckets['80-89']++;
          else if (s.avg >= 70) buckets['70-79']++;
          else if (s.avg >= 60) buckets['60-69']++;
          else buckets['Below 60']++;
        });

        result.subjectWise = {
          labels: Object.keys(buckets),
          datasets: [{ label: 'Students', data: Object.values(buckets), backgroundColor: '#1976d2' }],
        };

        // average marks across students
        const avgMarks = computed.length ? Math.round(computed.reduce((s, x) => s + x.avg, 0) / computed.length) : 0;
        result.stats = result.stats || {};
        result.stats.avgMarks = avgMarks;

        // Build classPerformance trend from exam data (group by exam name for dynamic labels)
        const byExam = {};
        rows.forEach((r) => {
          const examKey = r.examName || r.exam?.name || (r.examId ? `Exam ${r.examId}` : "Overall");
          if (!byExam[examKey]) byExam[examKey] = { total: 0, max: 0, count: 0, examDate: r.examDate };
          const marks = Number(r.marksObtained ?? r.marks ?? r.score ?? 0);
          const max = Number(r.maxMarks ?? r.max ?? r.total ?? 100);
          byExam[examKey].total += marks;
          byExam[examKey].max += max;
          byExam[examKey].count += 1;
        });
        const examLabels = Object.keys(byExam).sort((a, b) => {
          const da = byExam[a]?.examDate ? new Date(byExam[a].examDate).getTime() : 0;
          const db = byExam[b]?.examDate ? new Date(byExam[b].examDate).getTime() : 0;
          return da - db;
        });
        const examAvgs = examLabels.map((k) => {
          const v = byExam[k];
          return v.max > 0 ? Math.round((v.total / v.max) * 100) : 0;
        });
        if (examLabels.length > 0) {
          result.classPerformance = {
            labels: examLabels,
            datasets: [
              {
                label: "Class Average",
                data: examAvgs,
                borderColor: "#1976d2",
                backgroundColor: "rgba(25, 118, 210, 0.1)",
                fill: true,
              },
            ],
          };
        }
      }
    } catch (e) {
      // ignore
    }

    // If no exam rows found, try derive topStudents from assignment submissions
    try {
      if ((!result.topStudents || result.topStudents.length === 0) && result.assignments && Array.isArray(result.assignments) && result.assignments.length) {
        // aggregate scores per student from assignment submissions
        const byStudent = {};
        result.assignments.forEach((sub) => {
          const sid = sub.studentId || sub.Student?.id || sub.Student?.user?.id || sub.student?.id;
          const u = sub.Student?.user;
          const name = u?.fullName || [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim() || sub.studentName || sub.Student?.fullName || sub.student?.fullName || "Unknown";
          // try score and maxScore fields
          const score = Number(sub.score ?? sub.scoreGiven ?? sub.scoreValue ?? 0);
          const max = Number(sub.maxScore ?? sub.maxMarks ?? sub.Assignment?.maxMarks ?? 0);
          if (!byStudent[sid]) byStudent[sid] = { id: sid, name, total: 0, max: 0, count: 0 };
          if (!isNaN(score) && !isNaN(max) && max > 0) {
            byStudent[sid].total += score;
            byStudent[sid].max += max;
            byStudent[sid].count += 1;
          }
        });

        const computed = Object.values(byStudent).map((s) => ({
          id: s.id,
          name: s.name,
          avg: s.max ? Math.round((s.total / s.max) * 100) : 0,
        })).sort((a,b) => b.avg - a.avg);

        // Resolve "Unknown" from result.students
        computed.forEach((c) => {
          if (!c.name || c.name === "Unknown") {
            const found = result.students?.find((st) => (st.id || st.studentId) === c.id);
            if (found) {
              c.name = found.fullName || found.name || found.user?.fullName || [found.firstName, found.lastName].filter(Boolean).join(" ").trim() || c.name;
            }
          }
        });

        if (computed.length) {
          result.topStudents = computed.slice(0,10);
          // grade buckets
          const buckets = { '90-100': 0, '80-89': 0, '70-79': 0, '60-69': 0, 'Below 60': 0 };
          computed.forEach((s) => {
            if (s.avg >= 90) buckets['90-100']++;
            else if (s.avg >= 80) buckets['80-89']++;
            else if (s.avg >= 70) buckets['70-79']++;
            else if (s.avg >= 60) buckets['60-69']++;
            else buckets['Below 60']++;
          });
          result.subjectWise = {
            labels: Object.keys(buckets),
            datasets: [{ label: 'Students', data: Object.values(buckets), backgroundColor: '#1976d2' }],
          };
          const avgMarks = computed.length ? Math.round(computed.reduce((s,x) => s + x.avg, 0) / computed.length) : 0;
          result.stats = result.stats || {};
          result.stats.avgMarks = avgMarks;
        }
      }
    } catch (e) {
      // ignore
    }

    // ensure defaults
    result.stats = result.stats || {};
    result.stats.totalStudents = result.stats.totalStudents ?? (result.students?.length || 0);
    result.stats.avgAttendance = result.stats.avgAttendance ?? 0;
    result.stats.avgMarks = result.stats.avgMarks ?? 0;
    result.stats.assignmentsCompleted = result.stats.assignmentsCompleted ?? 0;

    return { data: result };
  },
};

export default reportService;
